import { h as __h } from "preact";
import { effect } from "@preact/signals";
import { Component, h } from "preact";
/** @import { AnyComponent, ComponentChildren } from "preact"; */

import { orgResponse } from "~/backend/validate.js";
/** @import { sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { query, paginatedResponse } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import { token } from "~/lib/session.js";
import * as msg from "~/lib/ws/msg.js";
import WS from "~/lib/ws/ws.js";
import html from "~/util/html.js";
import { navigate, pathname, Route, Router } from "~/util/router.js";
/** @import { RouteParams } from "~/util/router.js"; */
import Title from "~/util/title.js";
import Avatar from "~/component/Avatar.js";
import Dropdown from "~/component/Dropdown.js";
import { errorBoundary } from "~/component/ErrorBoundary.js";
import ErrorPage from "~/component/ErrorPage.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import Navigation from "~/component/Navigation.js";
import UserDropdown from "~/component/UserDropdown.js";
import Breadcrumbs, { Crumb } from "~/admin/component/Breadcrumbs.js";
import Switcher from "~/admin/component/Switcher.js";
/** @import { Section as SwitcherSection } from "~/admin/component/Switcher.js"; */

import { wsHost } from "../env.js";
import EnterpriseAuditLogs from "./enterprise/AuditLogs.js";
import EnterpriseBilling from "./enterprise/Billing.js";
import EnterpriseInvites from "./enterprise/Invites.js";
import EnterpriseMembers from "./enterprise/Members.js";
import EnterpriseOrganizations from "./enterprise/Organizations.js";
import ScimMappings from "./enterprise/ScimMappings/ScimMappings.js";
import EnterpriseSettings from "./enterprise/Settings.js";
import OrgComputers from "./org/Computers/Computers.js";
import OrgGroups from "./org/Groups/Groups.js";
import OrgKeys from "./org/Keys/Keys.js";
import OrgMembers from "./org/Members/Members.js";
import OrgLocations from "./org/Relays/Locations.js";
import Settings from "./org/Settings.js";
import BusinessCard from "./personal/BusinessCard.js";
import PersonalComputers from "./personal/Computers.js";
import Roles from "~/admin/lib/roles.js";
import { createEnterprise } from "~/admin/res/enterprise/actions/createEnterprise.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Dashboard.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Dashboard.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<DashboardProps>}
 *
 * @typedef {RouteParams<{ orgId: string | undefined }>} DashboardProps
 */
export default class Dashboard extends Component {
  orgs = query(() => ({
    key: ["/ui/admin/orgs", {
      limit: 9999
    }]
  }), {
    schema: paginatedResponse(orgResponse)
  });
  render() {
    // reading the orgs starts their request alongside the session's rather than after it
    void this.orgs.data;
    if (identity.error) return __h(DashboardError, {});
    if (!identity.initialized) return __h(DashboardLoader, {});
    const enterprise = identity.enterprise;
    if (!enterprise || !hasAdminPanel()) {
      navigate("/web", {
        replace: true
      });
      return;
    }
    return __h(AdminDashboard, {
      enterprise: enterprise,
      orgs: this.orgs,
      params: this.props.params
    });
  }
}

/**
 * @extends {Component<AdminDashboardProps>}
 *
 * @typedef {object} AdminDashboardPropsBase
 * @property {sessionEnterpriseResponse} enterprise
 * @property {Dashboard["orgs"]} orgs
 *
 * @typedef {AdminDashboardPropsBase & DashboardProps} AdminDashboardProps
 */
class AdminDashboard extends Component {
  ws = new WS();

  /** @type {(() => void) | undefined} */
  #disconnect;

  /** @override */
  componentDidMount() {
    this.#disconnect = connectWS(this.ws, [msg.ADMIN_MACHINE_REFRESH, msg.ADMIN_CONNECTION_REFRESH, msg.INVITE_REFRESH]);
  }

  /** @override */
  componentWillUnmount() {
    this.#disconnect?.();
  }
  enterprisePages() {
    const enterprise = this.props.enterprise;
    /** @type {(Page & { hidden: boolean })[]} */
    const pages = [{
      icon: "users",
      label: "Organizations",
      component: EnterpriseOrganizations,
      path: "organizations",
      hidden: !identity.permission("enterprise", "orgs")
    }, {
      icon: "person",
      label: "Members",
      component: EnterpriseMembers,
      path: "members",
      hidden: !identity.permission("enterprise", "members")
    }, {
      icon: "email",
      label: "Invites",
      component: EnterpriseInvites,
      path: "invites",
      hidden: enterprise.is_scim_enabled || !identity.permission("enterprise", "members")
    }, {
      icon: "folder-import",
      label: "Directory Mappings",
      component: ScimMappings,
      path: "mappings",
      subroutes: [":scimGroupId"],
      hidden: !enterprise.is_workos_configured || !identity.permission("enterprise", "members")
    }, {
      icon: "inbox",
      label: "Billing",
      component: EnterpriseBilling,
      path: "billing",
      hidden: !identity.permission("enterprise", "billing")
    }, {
      icon: "ledger",
      label: "Audit Log",
      component: EnterpriseAuditLogs,
      path: "audit-log",
      hidden: !identity.permission("enterprise", "audit_logs")
    }, {
      icon: "gear",
      label: "Settings",
      component: EnterpriseSettings,
      path: "settings",
      hidden: !identity.permission("enterprise", "settings") && !identity.permission("enterprise", "api_keys") && !identity.permission("enterprise", "billing")
    }];
    return pages.filter(p => !p.hidden);
  }

  /** @param {orgResponse} org */
  orgPages(org) {
    const enterprise = this.props.enterprise;
    /** @type {(Page & { hidden: boolean })[]} */
    const pages = [{
      icon: "machine",
      label: "Computers",
      component: OrgComputers,
      path: "computers",
      subroutes: [":computerId"],
      hidden: !identity.permission(org.id, "machines"),
      props: {
        ws: this.ws
      }
    }, {
      icon: "person",
      label: "Members",
      component: OrgMembers,
      path: "members",
      subroutes: [":memberId"],
      hidden: !identity.permission(org.id, "members"),
      props: {
        ws: this.ws,
        enterprise
      }
    }, {
      icon: "users",
      label: "Groups",
      component: OrgGroups,
      path: "groups",
      subroutes: [":groupId"],
      hidden: !identity.permission(org.id, "groups"),
      props: {
        enterprise
      }
    }, {
      icon: "antenna",
      label: "Relays",
      component: OrgLocations,
      path: "relays",
      subroutes: [":locationId", ":locationId/:relayId"],
      hidden: !identity.permission(org.id, "locations"),
      props: {
        ws: this.ws
      }
    }, {
      icon: "key",
      label: "Keys",
      component: OrgKeys,
      path: "keys",
      hidden: !identity.permission(org.id, "machine_keys")
    }, {
      icon: "gear",
      label: "Settings",
      component: Settings,
      path: "settings",
      hidden: !identity.permission(org.id, "settings")
    }];
    return pages.filter(p => !p.hidden);
  }
  render() {
    const {
      enterprise,
      orgs: orgsQuery
    } = this.props;
    if (orgsQuery.error || Roles.error) return __h(DashboardError, {});
    if (!orgsQuery.data || !Roles.ready) return __h(DashboardLoader, {});
    const orgs = orgsQuery.data.data.filter(org => org.enterprise_id === enterprise.id);
    const org = orgs.find(org => org.id === this.props.params.value.orgId) ?? null;
    const enterprisePages = this.enterprisePages();
    if (!org && !enterprisePages.length) {
      navigate(orgs.length ? `/org/${orgs[0]?.id}` : "/web", {
        replace: true
      });
      return;
    }
    const pages = org ? this.orgPages(org) : enterprisePages;
    if (!pages.length) {
      navigate("/web", {
        replace: true
      });
      return;
    }
    return [__h(Title, {
      title: "Admin Panel"
    }), __h(Crumb, {
      hidden: !enterprisePages.length
    }, __h("a", {
      href: "/" + enterprisePages[0]?.path
    }, enterprise.name)), __h(Crumb, {
      hidden: !org
    }, org && __h(OrgDropdown, {
      org: org,
      orgs: orgs
    })), __h(DashboardLayout, {
      pages: pages,
      prefix: org ? `/org/${org.id}` : "",
      baseProps: org ? {
        key: org.id,
        org
      } : {
        key: enterprise.id,
        enterprise
      },
      navKey: org ? "org" : "ent",
      switcher: __h(Switcher, {
        current: ADMIN_SECTION,
        sections: hasWebConsole() ? SECTIONS : []
      }),
      header: __h(Breadcrumbs, {})
    })];
  }
}

/**
 * @typedef {object} Page
 * @property {string} icon
 * @property {string} label
 * @property {AnyComponent<any>} component
 * @property {string} path The canonical URL segment for the nav link, or empty for the section's
 *   root.
 * @property {string[]} [subroutes] Additional sub-paths (relative to `path`) that mount the
 *   component.
 * @property {object} [props]
 */

/**
 * The sidebar and content frame shared by the admin panel and the web console: each page gets a nav
 * link under `prefix` and a route for its path and subroutes, and a path under `prefix` that
 * matches no page redirects to the first one. The current page's label is the document title,
 * which detail views override with the name of what they show.
 *
 * @typedef {object} DashboardLayoutProps
 * @property {Page[]} pages
 * @property {string} prefix
 * @property {object} [baseProps] Props passed to every page, under each page's own `props`.
 * @property {string} [navKey] Remounts the nav links (replaying their entrance) when it changes.
 * @property {ComponentChildren} switcher
 * @property {ComponentChildren} [aside] Shown in the sidebar below the nav links.
 * @property {ComponentChildren} [header] Shown in the content area above the page.
 * @param {DashboardLayoutProps} props
 */
function DashboardLayout({
  pages,
  prefix,
  baseProps,
  navKey,
  switcher,
  aside,
  header
}) {
  /** @type {import("~/component/Navigation.js").NavLink[]} */
  const links = pages.map(p => ({
    href: p.path ? `${prefix}/${p.path}` : prefix,
    icon: p.icon,
    label: p.label,
    wild: true
  }));
  const segment = pathname.value.slice(prefix.length).split("/")[1] ?? "";
  const page = pages.find(p => p.path === segment);
  if (!page) {
    navigate(links[0]?.href ?? prefix, {
      replace: true
    });
    return null;
  }

  /** @type {ComponentChildren[]} */
  const routes = pages.flatMap(p => {
    const props = {
      ...baseProps,
      ...p.props
    };
    const component = errorBoundary(p.component);
    const paths = [p.path, ...(p.subroutes ?? []).map(sub => `${p.path}/${sub}`)];
    return paths.map(path => h(Route, {
      path: `/${path}`,
      component,
      props
    }));
  });
  return [__h(Title, {
    title: page.label
  }), __h("div", {
    "class": css.dashboard
  }, __h("div", {
    "class": css.nav
  }, switcher, __h("div", {
    key: navKey,
    "class": css.navlinks
  }, __h(Navigation, {
    links: links,
    activePath: pathname.value
  }), aside), __h("div", {}, __h(UserDropdown, {
    modal: true
  }))), __h("div", {
    "class": css.content
  }, header, __h("main", {
    "class": css.main
  }, h(Router, null, routes))))];
}

/**
 * @extends {Component<OrgDropdownProps>}
 *
 * @typedef {object} OrgDropdownProps
 * @property {orgResponse[]} orgs
 * @property {orgResponse} org
 */
class OrgDropdown extends Component {
  render() {
    if (this.props.orgs.length <= 1) {
      return __h("div", {}, __h(Avatar, {
        size: 20,
        name: this.props.org.name
      }), this.props.org.name);
    }
    return __h(Dropdown, {
      id: "org-dropdown",
      placement: "bottom-start",
      trigger: __h("button", {
        type: "button",
        popovertarget: "org-dropdown"
      }, __h(Avatar, {
        size: 20,
        name: this.props.org.name
      }), this.props.org.name, __h("div", {
        "class": css.arrow
      }, __h(Icon, {
        name: "down-filled",
        size: 14
      }))),
      items: this.props.orgs.map(o => ({
        text: __h("div", {
          "class": css.orgDropdownItem
        }, __h(Avatar, {
          size: 20,
          name: o.name
        }), o.name),
        href: `/org/${o.id}/${pathname.value.replace(`/org/${this.props.org.id}/`, "")}`
      }))
    });
  }
}

/** The signed-in user's own computers, outside any enterprise they administer. */
export class PersonalDashboard extends Component {
  ws = new WS();

  /** @type {Page[]} */
  #pages = [{
    icon: "machine",
    label: "Computers",
    component: PersonalComputers,
    path: "",
    props: {
      ws: this.ws
    }
  }];

  /** @type {(() => void) | undefined} */
  #disconnect;

  /** @override */
  componentDidMount() {
    this.#disconnect = connectWS(this.ws, [msg.HOST_REFRESH, msg.MACHINE_ACCESS_INVITE_REFRESH]);
  }

  /** @override */
  componentWillUnmount() {
    this.#disconnect?.();
  }
  render() {
    if (identity.error) return __h(DashboardError, {});
    if (!identity.initialized) return __h(DashboardLoader, {});

    // the admin panel sends people who administer nothing here, so only send back those it can show
    if (!hasWebConsole() && hasAdminPanel()) {
      navigate("/", {
        replace: true
      });
      return;
    }
    const sections = !identity.enterprise ? SETUP_SECTIONS : hasAdminPanel() ? SECTIONS : [];
    return __h(DashboardLayout, {
      pages: this.#pages,
      prefix: "/web",
      switcher: __h(Switcher, {
        current: PERSONAL_SECTION,
        sections: sections
      }),
      aside: __h(BusinessCard, {})
    });
  }
}
function DashboardError() {
  return __h(ErrorPage, {
    title: "Couldn't load dashboard",
    icon: "nowifi"
  }, __h("p", {}, "Please check your network connection and try again."));
}
function DashboardLoader() {
  return __h("div", {
    "class": css.centered
  }, __h(Loader, {
    size: 32
  }));
}

/**
 * Connects `ws` for the given message subscriptions and returns a disposer that disconnects it.
 *
 * @param {WS} ws
 * @param {string[]} subscriptions
 * @returns {() => void}
 */
function connectWS(ws, subscriptions) {
  return effect(() => {
    if (token.value) ws.connect(wsHost, token.value, subscriptions);
    return () => ws.disconnect(false);
  });
}

/** @type {SwitcherSection} */
const ADMIN_SECTION = {
  label: "Admin Panel",
  description: "Manage your organization's people and computers.",
  icon: "office",
  href: "/"
};

/** @type {SwitcherSection} */
const PERSONAL_SECTION = {
  label: "Web Console",
  description: "Connect to and share your own computers.",
  icon: "app",
  href: "/web"
};
const SECTIONS = [ADMIN_SECTION, PERSONAL_SECTION];

/** @type {SwitcherSection[]} */
const SETUP_SECTIONS = [{
  label: "Set up your business",
  description: "Manage your team's computers and access in one place.",
  icon: "office",
  onSelect: createEnterprise
}];
function hasAdminPanel() {
  return identity.canAccessEnterprise() || Object.keys(identity.permissions.orgs).some(orgId => identity.canAccessOrg(orgId));
}
function hasWebConsole() {
  return !identity.enterprise || Object.keys(identity.permissions.orgs).length > 0;
}